// The stalled-queue alarm's face (`apps/worker/src/cron/stalled-queue.ts`
// `stalledQueueNotification`, #3769): the account's oldest held (or merely queued)
// work, how long it has waited, and what to do about it.
//
// THE REMEDY IS THE DASHBOARD'S OWN. The server appends `TASK_STALL_REMEDY[kind]`, the
// domain's English operator prose (MCP verbs, internal constants), which
// `scripts/stall-remedy-not-rendered.test.ts` keeps off every localized surface. So each
// stall has a short remedy written for a person here, keyed by the same `TaskStallKind`
// the wire carries. `fleet.top.tiles.held_reason.*` (`lib/fleet-queue-line.ts`) names
// each stall's CAUSE in a few words and says nothing of a fix, so it is not reused.

import type { TaskStallKind } from '@developerz/domain';
import { z } from 'zod';
import { count, face, type Renderer } from './notification-copy-face';

/**
 * One remedy per stall, plus `queued` (work nothing holds, waiting for a machine).
 * TOTAL over the domain's union, so a stall added there fails to compile until it has
 * copy; literal keys, so the i18n coverage scan sees each.
 */
export const STALL_REMEDY_KEY = {
  queued: 'notifications.kind.decision_needed.stalled_queue.remedy_queued',
  dependency_failed: 'notifications.kind.decision_needed.stalled_queue.remedy_dependency_failed',
  blocked_on_answer: 'notifications.kind.decision_needed.stalled_queue.remedy_blocked_on_answer',
  upstream_group_unmerged:
    'notifications.kind.decision_needed.stalled_queue.remedy_upstream_group_unmerged',
  dependency_pending: 'notifications.kind.decision_needed.stalled_queue.remedy_dependency_pending',
  dependency_unreleasable:
    'notifications.kind.decision_needed.stalled_queue.remedy_dependency_unreleasable',
  no_box_on_account: 'notifications.kind.decision_needed.stalled_queue.remedy_no_box_on_account',
  no_usable_keys: 'notifications.kind.decision_needed.stalled_queue.remedy_no_usable_keys',
  area_busy: 'notifications.kind.decision_needed.stalled_queue.remedy_area_busy',
  repo_not_onboarded: 'notifications.kind.decision_needed.stalled_queue.remedy_repo_not_onboarded',
  no_converse_capacity:
    'notifications.kind.decision_needed.stalled_queue.remedy_no_converse_capacity',
  no_service_capacity:
    'notifications.kind.decision_needed.stalled_queue.remedy_no_service_capacity',
  seat_capped: 'notifications.kind.decision_needed.stalled_queue.remedy_seat_capped',
  pool_cooling: 'notifications.kind.decision_needed.stalled_queue.remedy_pool_cooling',
  credential_cooling: 'notifications.kind.decision_needed.stalled_queue.remedy_credential_cooling',
  box_fault_excluded: 'notifications.kind.decision_needed.stalled_queue.remedy_box_fault_excluded',
  mode_disabled: 'notifications.kind.decision_needed.stalled_queue.remedy_mode_disabled',
  no_role_machine: 'notifications.kind.decision_needed.stalled_queue.remedy_no_role_machine',
  repo_env_fault: 'notifications.kind.decision_needed.stalled_queue.remedy_repo_env_fault',
  resume_backoff: 'notifications.kind.decision_needed.stalled_queue.remedy_resume_backoff',
  key_saturated: 'notifications.kind.decision_needed.stalled_queue.remedy_key_saturated',
  selection_waiting: 'notifications.kind.decision_needed.stalled_queue.remedy_selection_waiting',
  pr_quiet_window: 'notifications.kind.decision_needed.stalled_queue.remedy_pr_quiet_window',
  env_fault_exhausted:
    'notifications.kind.decision_needed.stalled_queue.remedy_env_fault_exhausted',
  plan_paused: 'notifications.kind.decision_needed.stalled_queue.remedy_plan_paused',
  machine_pinned: 'notifications.kind.decision_needed.stalled_queue.remedy_machine_pinned',
} as const satisfies Readonly<Record<TaskStallKind | 'queued', string>>;

const STALLS = Object.keys(STALL_REMEDY_KEY) as [
  keyof typeof STALL_REMEDY_KEY,
  ...(keyof typeof STALL_REMEDY_KEY)[],
];

/** `apps/worker/src/cron/stalled-queue.ts` `stalledQueueNotification`. */
const stalledQueue = face(
  z.object({
    face: z.literal('stalled_queue'),
    stall_kind: z.enum(STALLS),
    tasks: count,
    waiting_hours: count,
  }),
  (a, tr) => {
    const hours = tr.plural(
      'notifications.kind.decision_needed.stalled_queue.hours',
      a.waiting_hours,
    );
    const vars = { hours, remedy: tr(STALL_REMEDY_KEY[a.stall_kind]) };
    // "Held" is never said of the queued bucket: nothing holds that work.
    return a.stall_kind === 'queued'
      ? {
          title: tr('notifications.kind.decision_needed.stalled_queue.title_queued', { hours }),
          body: tr.plural(
            'notifications.kind.decision_needed.stalled_queue.body_queued',
            a.tasks,
            vars,
          ),
        }
      : {
          title: tr('notifications.kind.decision_needed.stalled_queue.title_held', { hours }),
          body: tr.plural(
            'notifications.kind.decision_needed.stalled_queue.body_held',
            a.tasks,
            vars,
          ),
        };
  },
);

/** Merged into `NOTIFICATION_COPY_FACES` (`notification-copy.ts`). */
export const STALL_FACES = {
  decision_needed: { stalled_queue: stalledQueue },
} as const satisfies Readonly<Record<string, Readonly<Record<string, Renderer>>>>;
